import { apiClient, registerAuthService } from './apiClient';
import { User } from '../types/auth';
import { Logger } from '../utils/logger';
import type { SupportedLanguage } from '../i18n';

export interface LoginCredentials {
  email: string;
  password: string;
}

// V4-FRONT-ANY-USAGE batch 6 2026-05-29 — réponse snake_case du backend /auth/me
interface MeResponse {
  id: string;
  email: string;
  full_name?: string;
  avatar_url?: string;
  role?: User['role'] | string;
  account_type?: 'USER' | 'AGENCY' | 'GUEST';
  language?: string;
  billing_interval?: User['billingInterval'];
  subscription_status?: User['subscriptionStatus'];
  subscription_ends_at?: User['subscriptionEndsAt'];
  stripe_payment_status?: User['stripePaymentStatus'];
  payment_failed_at?: User['paymentFailedAt'];
}

export interface RegisterData {
  email: string;
  password: string;
  full_name: string;
}

export interface RegisterWithPlanData {
  email: string;
  password: string;
  full_name?: string;
  plan_id: string;
  coupon_code?: string;
  billing_interval?: 'monthly' | 'annual';
  referral_code?: string;
  invitation_token?: string;
  // V4-EMAIL-SIGNUP-LANG — langue UI à l'inscription (localise le 1er email)
  language?: SupportedLanguage;
}

export interface RegisterWithPlanResponse {
  user_id: string;
  email: string;
  access_token: string;
  checkout_url: string | null;
  requires_email_verification?: boolean;
}

export interface AuthResponse {
  access_token: string;
  token_type: string;
}

// JWT helpers — centralisés dans jwtUtils.ts
import { isTokenExpired, getTokenExpiration } from '../utils/jwtUtils';

// Token stocké en sessionStorage (persistant dans l'onglet, effacé à la fermeture).
// Le backend pose également un cookie httpOnly sur /auth/token pour les requêtes HTTP
// (envoyé automatiquement par le navigateur avec withCredentials: true).
// Ce token sert uniquement à l'auth WebSocket (?token= query param) et à isAuthenticated().
// sessionStorage survit aux refreshs de page mais pas à la fermeture de l'onglet.
let _memToken: string | null = sessionStorage.getItem('raiso_session_token');

export const authService = {
  isAuthenticated(): boolean {
    if (!_memToken) return false;
    if (isTokenExpired(_memToken)) {
      Logger.warn('[AuthService] In-memory auth token expired');
      _memToken = null;
      return false;
    }
    return true;
  },

  getToken(): string | null {
    if (!_memToken || isTokenExpired(_memToken)) return null;
    return _memToken;
  },

  getTokenExpiration(): Date | null {
    if (!_memToken) return null;
    return getTokenExpiration(_memToken);
  },

  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    Logger.debug('[AuthService] Login request started', { email: credentials.email });
    const formData = new FormData();
    formData.append('username', credentials.email);
    formData.append('password', credentials.password);

    const response = await apiClient.post<AuthResponse>('/auth/token', formData, {
      headers: {
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });

    if (response.data.access_token) {
      _memToken = response.data.access_token;
      sessionStorage.setItem('raiso_session_token', _memToken);
      const expiration = getTokenExpiration(response.data.access_token);
      Logger.info('[AuthService] Login request succeeded', {
        email: credentials.email,
        expiresAt: expiration?.toISOString() ?? null,
      });
    }

    return response.data;
  },

  /**
   * Phase 1.2 — vérifie l'email ET ouvre la session via le token one-shot du
   * lien reçu par email (magic-link). Évite à l'utilisateur de re-saisir ses
   * identifiants après avoir cliqué. Le backend pose les cookies JWT ; on
   * stocke aussi le token en sessionStorage comme après un login classique.
   * Lève une erreur (410 expiré / 400 invalide) que l'appelant mappe vers un
   * message /login.
   */
  async verifyEmailAndLogin(token: string): Promise<AuthResponse> {
    const response = await apiClient.post<AuthResponse>('/auth/verify-email-and-login', { token });
    if (response.data.access_token) {
      _memToken = response.data.access_token;
      sessionStorage.setItem('raiso_session_token', _memToken);
    }
    return response.data;
  },

  async register(data: RegisterData): Promise<User> {
    Logger.info('[AuthService] Registration request started', { email: data.email });
    const response = await apiClient.post<User>('/auth/register', data);
    Logger.info('[AuthService] Registration request succeeded', { email: data.email });
    return response.data;
  },

  async registerWithPlan(data: RegisterWithPlanData): Promise<RegisterWithPlanResponse> {
    Logger.info('[AuthService] Registration-with-plan request started', {
      email: data.email,
      planId: data.plan_id,
      billingInterval: data.billing_interval ?? null,
    });
    const response = await apiClient.post<RegisterWithPlanResponse>('/auth/register-with-plan', data);
    if (response.data.access_token && !response.data.checkout_url) {
      _memToken = response.data.access_token;
      sessionStorage.setItem('raiso_session_token', _memToken);
    }
    Logger.info('[AuthService] Registration-with-plan request succeeded', {
      email: data.email,
      hasCheckoutUrl: !!response.data.checkout_url,
      requiresEmailVerification: !!response.data.requires_email_verification,
    });
    return response.data;
  },

  async checkEmail(email: string): Promise<boolean> {
    const response = await apiClient.post<{ exists: boolean }>('/auth/check-email', { email });
    return response.data.exists;
  },

  /**
   * Accepte une invitation guest : crée le compte (lecture seule) et auto-login.
   * Stocke le token comme un login normal. Renvoie le space_id de l'invité.
   */
  async acceptGuestInvitation(
    token: string,
    password: string,
    fullName?: string,
    language?: SupportedLanguage,
  ): Promise<{ user_id: string; space_id: string; account_type: string }> {
    const response = await apiClient.post<{
      user_id: string;
      access_token: string;
      space_id: string;
      account_type: string;
    }>('/auth/guest/accept-invitation', {
      token,
      password,
      full_name: fullName,
      language,
    });
    if (response.data.access_token) {
      _memToken = response.data.access_token;
      sessionStorage.setItem('raiso_session_token', _memToken);
    }
    Logger.info('[AuthService] Guest invitation accepted', { spaceId: response.data.space_id });
    return response.data;
  },

  async getCurrentUser(): Promise<User> {
    Logger.debug('[AuthService] Fetching current user profile');
    // V4-FRONT-ANY-USAGE batch 6 2026-05-29 — DTO snake_case du backend /auth/me
    const response = await apiClient.get<MeResponse>('/auth/me');
    // Map backend snake_case to frontend camelCase if needed
    const userData = response.data;
    return {
      id: userData.id,
      email: userData.email,
      fullName: userData.full_name ?? '',
      avatarUrl: userData.avatar_url,
      role: (userData.role || 'user') as User['role'],
      accountType: userData.account_type,
      language: userData.language,
      billingInterval: userData.billing_interval,
      subscriptionStatus: userData.subscription_status,
      subscriptionEndsAt: userData.subscription_ends_at,
      stripePaymentStatus: userData.stripe_payment_status,
      paymentFailedAt: userData.payment_failed_at,
    };
  },

  /**
   * V4-AUTH-SERVICE-SWALLOWS-ERROR-RESIDUAL 2026-07-30 — lève désormais au lieu de
   * renvoyer `false`. Les 2 appelants (`SignUpForm`, `SignInForm`) avaient DÉJÀ un
   * `catch` qui affiche `verificationEmailResendError` : il était inatteignable, et
   * un échec d'envoi passait pour un succès. `silent` car ces appelants rendent
   * eux-mêmes le toast d'erreur.
   */
  async resendVerificationEmail(email: string): Promise<void> {
    await apiClient.post('/auth/resend-verification', { email }, { metadata: { silent: true } });
    Logger.info('[AuthService] Verification email resent', { email });
  },

  /**
   * V4-AUTH-SERVICE-SWALLOWS-ERROR-RESIDUAL 2026-07-30 — lève désormais, pour un
   * contrat de service homogène. ⚠️ `ForgotPasswordPage` DOIT continuer d'afficher
   * le même écran de confirmation en cas d'échec : c'est à elle d'absorber l'erreur.
   *
   * PAS de `silent` ici, et c'est délibéré : l'anti-énumération est déjà assurée
   * **côté backend** (`app/routes/auth/password.py:44` — « Always returns success to
   * prevent user enumeration »), donc un email inconnu ne produit aucune erreur à
   * masquer. Le seul échec réaliste est le rate limit `3/hour` (429) : le rendre
   * silencieux afficherait « email envoyé » alors que rien n'est parti.
   */
  async forgotPassword(email: string): Promise<void> {
    await apiClient.post('/auth/forgot-password', { email });
    Logger.info('[AuthService] Forgot-password request succeeded', { email });
  },

  /**
   * V4-RESET-PWD-SERVICE-SWALLOWS-ERROR 2026-07-30 — n'avale plus l'erreur.
   * L'ancienne signature `Promise<boolean>` masquait le `detail` du backend
   * (`expired_token` / `invalid_token`), ce qui rendait inatteignable le branching
   * de `ResetPasswordPage` et ses messages i18n dédiés : l'utilisateur voyait
   * toujours l'erreur générique. L'appelant catch et mappe désormais lui-même.
   * `silent` car cette UI d'erreur est rendue inline (avec CTA « nouveau lien ») :
   * sans lui l'intercepteur ajouterait un toast redondant.
   */
  async resetPassword(token: string, newPassword: string): Promise<void> {
    await apiClient.post(
      '/auth/reset-password',
      { token, new_password: newPassword },
      { metadata: { silent: true } },
    );
    Logger.info('[AuthService] Password reset succeeded');
  },

  /**
   * V4-AUTH-SERVICE-SWALLOWS-ERROR-RESIDUAL 2026-07-30 — lève désormais. Pas de
   * `silent` ici, volontairement : l'appelant (`App.tsx`) ne rend aucune UI d'erreur,
   * donc on laisse l'intercepteur apiClient afficher le toast. Avant, un lien
   * d'invitation périmé échouait dans un silence total.
   */
  async acceptInvitation(invitationToken: string): Promise<void> {
    await apiClient.post('/auth/accept-invitation', { invitation_token: invitationToken });
    Logger.info('[AuthService] Invitation accepted');
  },

  async updateLanguage(language: SupportedLanguage): Promise<void> {
    Logger.debug('[AuthService] Persisting language preference', { language });
    await apiClient.patch('/auth/me', { language });
  },

  /**
   * V4-AUTH-401-FORCE-LOGOUT (2026-07-03) — refresh silencieux de la session via
   * le cookie httpOnly `refresh_token` (7j, posé au login). Appelé par
   * l'intercepteur apiClient sur un 401 applicatif AVANT de conclure à une vraie
   * expiration : la plupart des 401 sont transitoires (cookie access_token
   * brièvement expiré, clock skew, backend en cours de redémarrage). En cas de
   * succès, le nouvel access_token est stocké et la requête d'origine est rejouée.
   *
   * `metadata.silent` évite un toast d'erreur si le refresh échoue (l'intercepteur
   * enchaîne alors sur le logout). Ne jamais laisser une exception remonter : on
   * renvoie simplement false pour que l'intercepteur bascule proprement en logout.
   */
  async refreshSession(): Promise<boolean> {
    try {
      const response = await apiClient.post<AuthResponse>(
        '/auth/refresh',
        undefined,
        { metadata: { silent: true } },
      );
      if (response.data?.access_token) {
        _memToken = response.data.access_token;
        sessionStorage.setItem('raiso_session_token', _memToken);
        Logger.info('[AuthService] Session refreshed silently');
        return true;
      }
      return false;
    } catch (error) {
      Logger.warn('[AuthService] Silent session refresh failed', {
        error: error instanceof Error ? error.message : 'unknown_error',
      });
      return false;
    }
  },

  logout() {
    Logger.info('[AuthService] Clearing auth token');
    _memToken = null;
    sessionStorage.removeItem('raiso_session_token');
    // Effacement du cookie httpOnly côté serveur en fire-and-forget
    apiClient.post('/auth/logout').catch(() => {});
  }
};

// Enregistrer authService dans apiClient après l'initialisation (évite la dépendance circulaire)
registerAuthService(authService);
